/**
 * Componente ActionButtonGroup - Grupo de Acciones Reutilizable
 * Agrupa acciones separando acciones destructivas de acciones normales
 * ESTÁNDAR: Usar en toda la aplicación para grupos de acciones
 */

import React from 'react';
import { Box, IconButton, Tooltip, Divider, useTheme } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

interface Action {
  /**
   * Icono de la acción (componente de MUI Icons)
   */
  icon: React.ReactElement;
  
  /**
   * Tooltip a mostrar
   */
  tooltip: string;
  
  /**
   * Función a ejecutar al hacer clic
   */
  onClick: () => void;
  
  /**
   * Color de la acción (primary, error, warning, etc.)
   */
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  
  /**
   * Si es true, es una acción destructiva (se separa visualmente)
   */
  destructive?: boolean;
  
  /**
   * Si es true, la acción está deshabilitada
   */
  disabled?: boolean;
}

interface ActionButtonGroupProps {
  /**
   * Array de acciones a mostrar
   */
  actions: Action[];
  
  /**
   * Tamaño de los iconos
   */
  size?: 'small' | 'medium';
  
  /**
   * Estilos personalizados
   */
  sx?: SxProps<Theme>;
}

export const ActionButtonGroup: React.FC<ActionButtonGroupProps> = ({
  actions,
  size = 'small',
  sx,
}) => {
  const theme = useTheme();
  
  // Separar acciones normales de destructivas
  const normalActions = actions.filter((action) => !action.destructive);
  const destructiveActions = actions.filter((action) => action.destructive);
  
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        '& > *:not(:last-child):not(.MuiDivider-root)': {
          borderRight: '1px solid',
          borderColor: 'divider',
          pr: 0.5,
        },
        ...sx,
      }}
    >
      {/* Acciones normales */}
      {normalActions.map((action, index) => (
        <Tooltip key={`normal-${index}`} title={action.tooltip} arrow>
          <span>
            <IconButton
              size={size}
              onClick={action.onClick}
              color={action.color || 'primary'}
              disabled={action.disabled}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              {action.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
      
      {/* Separador visual si hay acciones destructivas */}
      {normalActions.length > 0 && destructiveActions.length > 0 && (
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            mx: 0.5,
            borderColor: theme.palette.error.main,
            opacity: 0.3,
          }}
        />
      )}
      
      {/* Acciones destructivas */}
      {destructiveActions.map((action, index) => (
        <Tooltip key={`destructive-${index}`} title={action.tooltip} arrow>
          <span>
            <IconButton
              size={size}
              onClick={action.onClick}
              color={action.color || 'error'}
              disabled={action.disabled}
              sx={{
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              {action.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Box>
  );
};

